import type { TourValidationError } from '@/features/tour/tour-form-model'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/button'

interface StepFooterProps {
  isFirstStep: boolean
  isLastStep: boolean
  /** Why "Next" is disabled; `null` when the current step is complete. */
  blockedBy: TourValidationError | null
  isSaving: boolean
  onBack: () => void
  onNext: () => void
  onSubmit: () => void
}

/** Create flow only: Back / Next between steps, and the final "Create" button, with the reason when blocked. */
export function StepFooter({
  isFirstStep,
  isLastStep,
  blockedBy,
  isSaving,
  onBack,
  onNext,
  onSubmit,
}: StepFooterProps) {
  const { t } = useTranslation()
  const hint = blockedBy
    ? blockedBy.defaultValue
      ? t(blockedBy.key, { defaultValue: blockedBy.defaultValue })
      : t(blockedBy.key)
    : null

  return (
    <div className="sticky bottom-0 z-10 mt-6 flex items-center justify-between gap-4 border-t border-zinc-200 bg-white py-4">
      <Button variant="outline" onClick={onBack} disabled={isFirstStep || isSaving}>
        <ArrowLeft />
        {t('common.back')}
      </Button>
      <div className="flex min-w-0 items-center gap-4">
        {hint && (
          <p role="status" className="truncate text-sm text-amber-700">
            {hint}
          </p>
        )}
        {isLastStep ? (
          <Button onClick={onSubmit} disabled={Boolean(blockedBy) || isSaving}>
            {isSaving && <Loader2 className="animate-spin" />}
            {t('tour.createAction', { defaultValue: 'Tạo lộ trình' })}
          </Button>
        ) : (
          <Button onClick={onNext} disabled={Boolean(blockedBy)}>
            {t('common.next', { defaultValue: 'Tiếp tục' })}
            <ArrowRight />
          </Button>
        )}
      </div>
    </div>
  )
}
